import { formatCompletedDate } from "@/shared/utils/date";
import { TodoItem } from "../types";
import { TodoListItem } from "./TodoListItem";
import { motion } from "framer-motion";

type GroupByType = "dueDate" | "doneDate" | null;

interface TodosListProps {
  items: TodoItem[];
  onToggleDone?: (id: number) => void;
  className?: string;
  isReadOnly?: boolean;
  showDue?: boolean;
  onTodoPress?: (id: number) => void;
  onRemove?: (id: number) => void;
  groupBy?: GroupByType;
}

interface GroupedTodoSection {
  key: string;
  items: TodoItem[];
}

function getGroupKey(item: TodoItem, groupBy: GroupByType): string {
  if (groupBy === "dueDate" && item.dueDate) {
    const date = item.dueDate instanceof Date ? item.dueDate : new Date(item.dueDate);
    return formatCompletedDate(date);
  }

  if (groupBy === "doneDate" && item.doneAt) {
    const date = item.doneAt instanceof Date ? item.doneAt : new Date(item.doneAt);
    return formatCompletedDate(date);
  }

  return "No Date";
}

function groupTodos(items: TodoItem[], groupBy: GroupByType): GroupedTodoSection[] {
  if (!groupBy) {
    return [{ key: "", items }];
  }

  const sections: GroupedTodoSection[] = [];
  const sectionMap = new Map<string, TodoItem[]>();

  items.forEach((item) => {
    const key = getGroupKey(item, groupBy);
    if (!sectionMap.has(key)) {
      const sectionItems: TodoItem[] = [];
      sectionMap.set(key, sectionItems);
      sections.push({ key, items: sectionItems });
    }
    sectionMap.get(key)!.push(item);
  });

  return sections;
}

export function TodosList({
  items,
  onToggleDone,
  className,
  isReadOnly,
  showDue,
  onTodoPress,
  onRemove,
  groupBy = null,
}: TodosListProps) {
  const sections = groupTodos(items, groupBy);

  return (
    <div className={className}>
      {sections.map((section) => (
        <div key={section.key}>
          {groupBy && section.key && <span className="text-default-500 text-xs">{section.key}</span>}
          <motion.div layout>
            {section.items.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <TodoListItem
                  item={item}
                  onToggleDone={onToggleDone}
                  isReadOnly={isReadOnly}
                  showDue={showDue}
                  onPress={item.done ? undefined : onTodoPress}
                  onRemove={onRemove}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      ))}
    </div>
  );
}
